import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField } from "./FloatingField";
import { useStore } from "../store/globalState";
import {
  fallbackCanonicalName,
  parseRecipientName,
  type CanonicalName,
} from "../api/parse";
import {
  formatCanonicalName,
  type RecipientBookEntry,
  type RecipientDestination,
} from "../api/recipients";
import type { RecipientInput } from "../interfaces/payout";
import { useRecipients } from "../hooks/useRecipients";
import { recipientBookEntryMatchesCountry } from "../utils/recipient-destinations";
import { CloseLight } from "../assets/navIcons";

export interface RecipientDetailsSlotProps {
  value?: RecipientInput | null;
  destinationCountry: string;
  onClose: () => void;
  onSubmit: (
    recipient: RecipientInput,
    bookDestinations?: RecipientDestination[]
  ) => void;
}

const canonicalNamesEqual = (
  a: CanonicalName | null | undefined,
  b: CanonicalName | null | undefined
): boolean => {
  if (!a || !b) return false;
  return (
    (a.firstName ?? "").trim() === (b.firstName ?? "").trim() &&
    (a.middleName ?? "").trim() === (b.middleName ?? "").trim() &&
    (a.lastName ?? "").trim() === (b.lastName ?? "").trim() &&
    (a.secondLastName ?? "").trim() === (b.secondLastName ?? "").trim()
  );
};

const optionalDob = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

/**
 * Recipient identity: recent book picks + free-text full legal name normalized
 * via parse/name (positional fallback). Recent list scrolls; header, name
 * field and continue stay fixed (same pattern as the country picker).
 * Optional DOB improves OFAC precision; never required.
 */
export const RecipientDetailsSlot: React.FC<RecipientDetailsSlotProps> = ({
  value,
  destinationCountry,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { bodyCopy, backgroundBtn, textColorBtn } = colorConfig;
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };

  const { recipients, loading: loadingRecipients } = useRecipients();
  const [fullName, setFullName] = useState(value?.fullName ?? "");
  const [dateOfBirth, setDateOfBirth] = useState(value?.dateOfBirth ?? "");
  const [selectedKey, setSelectedKey] = useState<string | null>(
    value?.recipientKey ?? null
  );
  const [parsing, setParsing] = useState(false);

  const trimmed = fullName.trim();
  const valid = trimmed.split(/\s+/).length >= 2 && trimmed.length >= 3;

  // Shimmer only on the first load: a refresh keeps the current list visible
  // instead of collapsing it.
  const recentBusy = loadingRecipients && recipients.length === 0;

  const recent = recipients.filter(
    (r) =>
      r.name?.firstName?.trim() &&
      r.name?.lastName?.trim() &&
      recipientBookEntryMatchesCountry(r, destinationCountry)
  );

  // Re-open / parent value change: restore exclusive highlight.
  // Do not depend on the filtered `recent` array (new ref every render) or
  // typing would fight with the clear-on-edit path below.
  useEffect(() => {
    if (value?.recipientKey) {
      const keyedEntry = recipients.find(
        (entry) => entry.recipientKey === value.recipientKey
      );
      setSelectedKey(
        keyedEntry &&
          recipientBookEntryMatchesCountry(keyedEntry, destinationCountry)
          ? value.recipientKey
          : null
      );
      return;
    }
    if (!value?.canonicalName) {
      setSelectedKey(null);
      return;
    }
    const matches = recipients.filter(
      (r) =>
        r.name?.firstName?.trim() &&
        r.name?.lastName?.trim() &&
        recipientBookEntryMatchesCountry(r, destinationCountry) &&
        canonicalNamesEqual(r.name, value.canonicalName)
    );
    setSelectedKey(matches.length === 1 ? matches[0].recipientKey : null);
  }, [
    value?.recipientKey,
    value?.canonicalName,
    recipients,
    destinationCountry,
  ]);

  useEffect(() => {
    setDateOfBirth(value?.dateOfBirth ?? "");
  }, [value?.dateOfBirth]);

  const withDob = (
    recipient: RecipientInput,
    dob = dateOfBirth
  ): RecipientInput => {
    const next = optionalDob(dob);
    const { dateOfBirth: _prev, ...rest } = recipient;
    return next ? { ...rest, dateOfBirth: next } : rest;
  };

  const handleSelectRecent = (entry: RecipientBookEntry) => {
    if (parsing) return;
    const label = formatCanonicalName(entry.name);
    const dob = entry.dateOfBirth?.trim() ?? "";
    setSelectedKey(entry.recipientKey);
    setFullName(label);
    setDateOfBirth(dob);
    onSubmit(
      withDob(
        {
          fullName: label,
          canonicalName: entry.name,
          recipientKey: entry.recipientKey,
        },
        dob
      ),
      entry.destinations ?? []
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || parsing) return;

    // Same book pick re-submitted (name untouched) — keep key + habits.
    if (
      value?.canonicalName &&
      value.fullName === trimmed &&
      (selectedKey == null || selectedKey === value.recipientKey)
    ) {
      onSubmit(
        withDob({
          fullName: value.fullName,
          canonicalName: value.canonicalName,
          recipientKey: value.recipientKey ?? selectedKey ?? undefined,
        }),
        undefined
      );
      return;
    }

    // Typed name that still matches the highlighted book row exactly.
    if (selectedKey) {
      const fromBook = recent.find((r) => r.recipientKey === selectedKey);
      if (fromBook && formatCanonicalName(fromBook.name) === trimmed) {
        onSubmit(
          withDob({
            fullName: trimmed,
            canonicalName: fromBook.name,
            recipientKey: fromBook.recipientKey,
          }),
          fromBook.destinations ?? []
        );
        return;
      }
    }

    setParsing(true);
    try {
      const canonicalName =
        (await parseRecipientName(trimmed)) ?? fallbackCanonicalName(trimmed);
      setSelectedKey(null);
      onSubmit(
        withDob({
          fullName: trimmed,
          canonicalName,
          recipientKey: undefined,
        }),
        []
      );
    } finally {
      setParsing(false);
    }
  };

  return (
    <section className="payout-details-slot recipient-details-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" />
        <Title title={t("recipient.title")} align="center" color={bodyCopy} />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      <form className="payout-form" onSubmit={handleSubmit}>
        {recentBusy && (
          <section
            className="recipient-recent"
            aria-busy="true"
            aria-label={t("recipient.loadingRecent")}
          >
            <span className="payout-field-label">
              {t("recipient.recentLabel")}
            </span>
            <div className="recipient-recent-list" aria-hidden="true">
              {Array.from({ length: 2 }, (_, i) => (
                <div key={i} className="payout-saved-skeleton">
                  <span className="payout-saved-skeleton-line" />
                  <span className="payout-saved-skeleton-line short" />
                </div>
              ))}
            </div>
          </section>
        )}

        {!recentBusy && recent.length > 0 && (
          <section
            className="recipient-recent"
            aria-label={t("recipient.recentAria")}
          >
            <span className="payout-field-label">{t("recipient.recentLabel")}</span>
            <div role="list" className="recipient-recent-list">
              {recent.map((entry) => {
                const label = formatCanonicalName(entry.name);
                const selected = selectedKey === entry.recipientKey;
                return (
                  <button
                    key={entry.recipientKey}
                    type="button"
                    role="listitem"
                    aria-pressed={selected}
                    disabled={parsing}
                    className={`recipient-recent-item${selected ? " selected" : ""}`}
                    onClick={() => handleSelectRecent(entry)}
                  >
                    <span className="recipient-recent-name">{label}</span>
                    <span className="payout-hint">
                      {t("recipient.timesUsed", { count: entry.timesUsed })}
                    </span>
                  </button>
                );
              })}
            </div>
            <span className="payout-hint recipient-or-new">
              {t("recipient.orEnterNew")}
            </span>
          </section>
        )}

        <div className="payout-field">
          <FloatingField
            label={t("recipient.fullNameLabel")}
            value={fullName}
            colors={fieldColors}
          >
            <input
              type="text"
              autoComplete="name"
              autoCapitalize="words"
              style={{ color: bodyCopy }}
              value={fullName}
              onChange={(e) => {
                setFullName(e.target.value);
                // Editing free-text clears the book highlight so only one
                // selection can be active at a time. Drop book-prefilled DOB.
                if (selectedKey) {
                  setDateOfBirth("");
                }
                setSelectedKey(null);
              }}
              disabled={parsing}
            />
          </FloatingField>
          <span className="payout-hint">{t("recipient.fullNameHint")}</span>
        </div>

        <div className="payout-field">
          <FloatingField
            label={t("recipient.dateOfBirthLabel")}
            value={dateOfBirth || " "}
            colors={fieldColors}
          >
            <input
              type="date"
              name="dateOfBirth"
              autoComplete="bday"
              style={{ color: bodyCopy }}
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              disabled={parsing}
            />
          </FloatingField>
          <span className="payout-hint">{t("recipient.dateOfBirthHint")}</span>
        </div>

        <Button
          type="submit"
          size="large"
          disabled={!valid || parsing}
          background={backgroundBtn}
          color={textColorBtn}
        >
          {parsing ? t("recipient.checking") : t("common.continue")}
        </Button>
      </form>
    </section>
  );
};
